import { CourseNavigationItem } from '../../../../services/course-player.service';

/**
 * KAN-326 - "Require units in order" completion gate (frontend display).
 *
 * Mirrors the backend pure function (sequential-access + unit-order-lock): a
 * unit is locked until the IMMEDIATELY preceding unit (in player order) is
 * fully complete. A unit is complete when every playable item in it
 * (modules + end-of-unit quiz) has status 'completed'. The lock propagates
 * forward down the chain. Returns a Map of unitId -> lock reason for each
 * LOCKED unit (unlocked units are absent), or an empty Map when the setting
 * is off.
 *
 * This is DISPLAY only; the server still enforces the gate (module GET + quiz
 * submission return 403 UNIT_LOCKED). It sits beside the KAN-318 VS4 pass gate
 * (score based) - either gate can lock a unit.
 */
export function computeUnitOrderGateReasons(
  navigationItems: CourseNavigationItem[],
  requireUnitsInOrder: boolean
): Map<string, string> {
  const reasons = new Map<string, string>();
  if (!requireUnitsInOrder) return reasons;

  // Ordered, de-duplicated unit ids in player order, plus a display name.
  const orderedUnits: { unitId: string; unitName: string }[] = [];
  const seen = new Set<string>();
  for (const item of navigationItems as any[]) {
    const uid: string | undefined = item.parentUnitId || item.unitId;
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    // unit_intro items carry the unit's own name; fall back to a generic label.
    const name = item.type === 'unit_intro' ? item.name : item.unitName || 'the previous unit';
    orderedUnits.push({ unitId: uid, unitName: name });
  }

  const isUnitComplete = (unitId: string): boolean => {
    const playable = navigationItems.filter(
      (i) => i.isPlayable && (i as any).parentUnitId === unitId
    );
    // A unit with no playable items is vacuously complete.
    return playable.every((i) => i.status === 'completed');
  };

  for (let i = 0; i < orderedUnits.length; i++) {
    if (i === 0) continue; // first unit always unlocked
    const prev = orderedUnits[i - 1];
    const prevLocked = reasons.has(prev.unitId);
    const locked = !isUnitComplete(prev.unitId) || prevLocked;
    if (locked) {
      reasons.set(
        orderedUnits[i].unitId,
        `Complete "${prev.unitName}" before starting this unit.`
      );
    }
  }

  return reasons;
}
